import React, { Ref, useState } from 'react';
import { ExitIcon } from '../icons';
import { InlineSpinner } from '../common/spinner';
import { DrawerModal } from '../modals/drawer-modal';
import { PostCallForm, PostCallFormRef, PostCallFormSteps } from './post-call-form';
import { usePostCall } from '../../hooks/api/usePostCall';

export interface PostCallModalProps {
  postCallId?: number;
  open: boolean;
  onClose: () => void;
  formRef?: Ref<PostCallFormRef>;
}

export const PostCallModal: React.FC<PostCallModalProps> = ({ postCallId, open, onClose, formRef }) => {
  const [step, setStep] = useState<PostCallFormSteps>(PostCallFormSteps.DESCRIBE);
  const { data: postCall, isLoading } = usePostCall(postCallId!);

  const stepTitles = {
    [PostCallFormSteps.DESCRIBE]: 'Rate your call',
    [PostCallFormSteps.FEELING]: 'How are you feeling?',
    [PostCallFormSteps.CONNECT_AGAIN]: 'Connect again?',
    [PostCallFormSteps.FEEDBACK]: 'Your feedback',
    [PostCallFormSteps.THANK_YOU]: 'Thank you',
  };

  const header = (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        {step !== PostCallFormSteps.THANK_YOU && (
          <div className="flex items-center gap-2">
            {[...Array(4)].map((_, index) => (
              <div
                key={index}
                className={`h-2 w-2 rounded-full ${index <= step ? 'bg-accent' : 'bg-input-border-gray'}`}
              />
            ))}
          </div>
        )}
        <p className="text-label-gray font-['Albert_Sans'] text-lg font-semibold leading-6">{stepTitles[step]}</p>
      </div>
      <button
        onClick={onClose}
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition-colors hover:bg-gray-100"
        aria-label="Close modal"
      >
        <ExitIcon color="#22282F" />
      </button>
    </div>
  );

  const content = isLoading ? (
    <div className="flex h-64 w-full items-center justify-center">
      <InlineSpinner />
    </div>
  ) : (
    <PostCallForm formRef={formRef} step={step} setStep={setStep} postCall={postCall} onCancel={onClose} />
  );

  // For the DrawerModal, we need header, children, and footer
  // But our form already has its own footer (ButtonPanel)
  // So we'll use an empty footer in the drawer and let the form handle it
  return (
    <DrawerModal open={open} onClose={onClose} header={header} footer={<></>} maxWidth="max-w-[620px]">
      {content}
    </DrawerModal>
  );
};
